import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { dealRoomApi } from '../lib/api';
import { cn, formatRelativeTime } from '../lib/utils';
import { 
  ArrowLeft, Plus, Link, FileText, MessageSquare, Send, 
  Loader2, Sparkles, AlertCircle, CheckCircle, HelpCircle,
  TrendingUp, TrendingDown, Users, Target
} from 'lucide-react';

const DealRoomDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const chatEndRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [dealRoom, setDealRoom] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  const [generatingDashboard, setGeneratingDashboard] = useState(false);
  
  // Upload states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadType, setUploadType] = useState('text'); // text, url
  const [uploadData, setUploadData] = useState({ filename: '', content: '', url: '' });
  const [uploading, setUploading] = useState(false);

  const fetchDealRoom = async () => {
    try {
      const [roomRes, docsRes, chatRes] = await Promise.all([
        dealRoomApi.get(id),
        dealRoomApi.getDocuments(id),
        dealRoomApi.getChatHistory(id)
      ]);
      setDealRoom(roomRes.data);
      setDocuments(docsRes.data.documents || []);
      setChatHistory(chatRes.data.messages || []);
    } catch (err) {
      console.error('Failed to fetch deal room:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDealRoom();
  }, [id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      if (uploadType === 'url') {
        await dealRoomApi.ingestUrl(id, uploadData.url);
      } else {
        await dealRoomApi.uploadDocument(id, {
          filename: uploadData.filename,
          file_type: 'text',
          content: uploadData.content
        });
      }
      setShowUploadModal(false);
      setUploadData({ filename: '', content: '', url: '' });
      fetchDealRoom();
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateDashboard = async () => {
    setGeneratingDashboard(true);
    try {
      const response = await dealRoomApi.generateDashboard(id);
      setDealRoom({ ...dealRoom, dashboard: response.data });
    } catch (err) {
      console.error('Dashboard generation failed:', err);
    } finally {
      setGeneratingDashboard(false);
    }
  };

  const handleSendChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || sendingChat) return;

    const userMessage = chatInput;
    setChatInput('');
    setSendingChat(true);

    // Optimistically add user message
    setChatHistory([...chatHistory, { role: 'user', content: userMessage, created_at: new Date().toISOString() }]);

    try {
      const response = await dealRoomApi.chat(id, userMessage);
      setChatHistory(prev => [...prev, response.data]);
    } catch (err) {
      console.error('Chat failed:', err);
      setChatHistory(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.',
        created_at: new Date().toISOString()
      }]);
    } finally {
      setSendingChat(false);
    }
  };

  const dashboard = dealRoom?.dashboard;

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="h-screen flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 p-4 border-b border-zinc-800 bg-zinc-950">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/deal-rooms')}
                className="text-zinc-400 hover:text-zinc-50"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-medium font-['Outfit'] text-zinc-50">{dealRoom?.name}</h1>
                {dealRoom?.company_name && (
                  <p className="text-zinc-400 text-sm">{dealRoom.company_name}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline"
                    data-testid="add-document-btn"
                    className="border-zinc-800 text-zinc-400 hover:text-zinc-50"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Document
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                  <DialogHeader>
                    <DialogTitle className="text-zinc-50 font-['Outfit']">Add Document</DialogTitle>
                  </DialogHeader>
                  <Tabs value={uploadType} onValueChange={setUploadType} className="mt-4">
                    <TabsList className="bg-zinc-800 border-zinc-700">
                      <TabsTrigger value="text" className="data-[state=active]:bg-zinc-700">Text/Notes</TabsTrigger>
                      <TabsTrigger value="url" className="data-[state=active]:bg-zinc-700">URL/Link</TabsTrigger>
                    </TabsList>
                    <form onSubmit={handleUpload} className="mt-4 space-y-4">
                      {uploadType === 'text' ? (
                        <>
                          <Input
                            value={uploadData.filename}
                            onChange={(e) => setUploadData({ ...uploadData, filename: e.target.value })}
                            placeholder="Document name"
                            required
                            data-testid="input-doc-name"
                            className="bg-zinc-800 border-zinc-700 text-zinc-50"
                          />
                          <Textarea
                            value={uploadData.content}
                            onChange={(e) => setUploadData({ ...uploadData, content: e.target.value })}
                            placeholder="Paste deck content, meeting notes, or any text..."
                            rows={8}
                            required
                            data-testid="input-doc-content"
                            className="bg-zinc-800 border-zinc-700 text-zinc-50 resize-none"
                          />
                        </>
                      ) : (
                        <Input
                          value={uploadData.url}
                          onChange={(e) => setUploadData({ ...uploadData, url: e.target.value })}
                          placeholder="https://..."
                          required
                          data-testid="input-doc-url"
                          className="bg-zinc-800 border-zinc-700 text-zinc-50"
                        />
                      )}
                      <Button 
                        type="submit" 
                        disabled={uploading}
                        data-testid="submit-document-btn"
                        className="w-full bg-white text-zinc-900 hover:bg-zinc-200"
                      >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                        {uploading ? 'Processing...' : 'Add Document'}
                      </Button>
                    </form>
                  </Tabs>
                </DialogContent>
              </Dialog>
              
              <Button
                onClick={handleGenerateDashboard}
                disabled={generatingDashboard || documents.length === 0}
                data-testid="generate-dashboard-btn"
                className="bg-white text-zinc-900 hover:bg-zinc-200"
              >
                {generatingDashboard ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                {generatingDashboard ? 'Analyzing...' : 'Generate Analysis'}
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Dashboard */}
          <div className="flex-1 overflow-y-auto p-6 border-r border-zinc-800">
            {dashboard ? (
              <div className="space-y-6 max-w-3xl">
                {/* Company Snapshot */}
                <section>
                  <h2 className="text-lg font-medium font-['Outfit'] text-zinc-50 mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-400" />
                    Company Snapshot
                  </h2>
                  <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4 space-y-3">
                    {dashboard.company_summary && (
                      <p className="text-zinc-300 font-['Manrope']">{dashboard.company_summary}</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {dashboard.stage && (
                        <span className="px-2 py-1 text-xs font-mono text-emerald-400 bg-emerald-400/10 rounded-full border border-emerald-400/20">
                          {dashboard.stage}
                        </span>
                      )}
                      {dashboard.category && (
                        <span className="px-2 py-1 text-xs font-mono text-blue-400 bg-blue-400/10 rounded-full border border-blue-400/20">
                          {dashboard.category}
                        </span>
                      )}
                      {dashboard.business_model && (
                        <span className="px-2 py-1 text-xs font-mono text-zinc-400 bg-zinc-800 rounded-full">
                          {dashboard.business_model}
                        </span>
                      )}
                    </div>
                  </div>
                </section>

                {/* Team Snapshot */}
                {(dashboard.key_people?.length > 0 || dashboard.founder_market_fit_signals?.length > 0) && (
                  <section>
                    <h2 className="text-lg font-medium font-['Outfit'] text-zinc-50 mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-amber-400" />
                      Team
                    </h2>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4 space-y-3">
                      {dashboard.key_people?.map((person, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center text-zinc-500 text-xs font-mono">
                            {person.name?.charAt(0)}
                          </div>
                          <div>
                            <p className="text-zinc-50 text-sm">{person.name}</p>
                            <p className="text-zinc-500 text-xs">{person.role}</p>
                          </div>
                        </div>
                      ))}
                      {dashboard.founder_market_fit_signals?.length > 0 && (
                        <div className="pt-2 border-t border-zinc-800">
                          <p className="text-zinc-500 text-xs mb-2">Founder-Market Fit Signals</p>
                          <div className="flex flex-wrap gap-1">
                            {dashboard.founder_market_fit_signals.map((signal, i) => (
                              <span key={i} className="px-2 py-0.5 text-xs font-mono text-emerald-400 bg-emerald-400/10 rounded">
                                {signal}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* Investment View */}
                <section>
                  <h2 className="text-lg font-medium font-['Outfit'] text-zinc-50 mb-4">Investment View</h2>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Bull Case */}
                    {dashboard.bull_case?.length > 0 && (
                      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
                        <h3 className="text-sm font-medium text-emerald-400 mb-3 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" />
                          Bull Case
                        </h3>
                        <ul className="space-y-2">
                          {dashboard.bull_case.map((item, i) => (
                            <li key={i} className="text-zinc-400 text-sm flex items-start gap-2">
                              <CheckCircle className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Bear Case */}
                    {dashboard.bear_case?.length > 0 && (
                      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
                        <h3 className="text-sm font-medium text-rose-400 mb-3 flex items-center gap-2">
                          <TrendingDown className="w-4 h-4" />
                          Bear Case
                        </h3>
                        <ul className="space-y-2">
                          {dashboard.bear_case.map((item, i) => (
                            <li key={i} className="text-zinc-400 text-sm flex items-start gap-2">
                              <AlertCircle className="w-3.5 h-3.5 text-rose-400 mt-0.5 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </section>

                {/* Diligence Questions */}
                {dashboard.top_diligence_questions?.length > 0 && (
                  <section>
                    <h2 className="text-lg font-medium font-['Outfit'] text-zinc-50 mb-4 flex items-center gap-2">
                      <HelpCircle className="w-5 h-5 text-blue-400" />
                      Key Diligence Questions
                    </h2>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
                      <ul className="space-y-2">
                        {dashboard.top_diligence_questions.map((q, i) => (
                          <li key={i} className="text-zinc-400 text-sm flex items-start gap-2">
                            <span className="text-blue-400 font-mono text-xs w-5">{i + 1}.</span>
                            {q}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </section>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mb-4">
                  <FileText className="w-8 h-8 text-zinc-500" />
                </div>
                <h3 className="text-lg font-medium font-['Outfit'] text-zinc-50 mb-2">
                  {documents.length > 0 ? 'Ready to Analyze' : 'Add Documents'}
                </h3>
                <p className="text-zinc-400 font-['Manrope'] mb-6 max-w-md">
                  {documents.length > 0 
                    ? 'Click "Generate Analysis" to create your deal room dashboard.'
                    : 'Upload pitch decks, meeting notes, or paste links to get started.'}
                </p>
                {documents.length === 0 && (
                  <Button
                    onClick={() => setShowUploadModal(true)}
                    data-testid="empty-add-doc"
                    className="bg-white text-zinc-900 hover:bg-zinc-200"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Document
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Right Panel - Chat */}
          <div className="w-[400px] flex flex-col bg-zinc-900/50">
            <div className="p-4 border-b border-zinc-800">
              <h2 className="text-sm font-medium font-['Outfit'] text-zinc-50 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-400" />
                Deal Copilot
              </h2>
              <p className="text-zinc-500 text-xs mt-1">Ask questions about this deal</p>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {chatHistory.length === 0 ? (
                <div className="text-center text-zinc-500 text-sm py-8">
                  <p className="mb-4">Start a conversation about this deal.</p>
                  <div className="space-y-2">
                    {['What is this company\'s wedge?', 'Who are the key competitors?', 'What are the main risks?'].map((q, i) => (
                      <button
                        key={i}
                        onClick={() => setChatInput(q)}
                        className="block w-full text-left px-3 py-2 bg-zinc-800 rounded-md text-zinc-400 text-xs hover:bg-zinc-700 transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                chatHistory.map((msg, i) => (
                  <div
                    key={i}
                    className={cn(
                      "rounded-md p-3",
                      msg.role === 'user' 
                        ? 'bg-zinc-800 ml-8' 
                        : 'bg-blue-400/10 border border-blue-400/20 mr-8'
                    )}
                  >
                    <p className={cn(
                      "text-sm",
                      msg.role === 'user' ? 'text-zinc-300' : 'text-zinc-300'
                    )}>
                      {msg.content}
                    </p>
                  </div>
                ))
              )}
              {sendingChat && (
                <div className="flex items-center gap-2 text-zinc-500 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Thinking...
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input */}
            <form onSubmit={handleSendChat} className="p-4 border-t border-zinc-800">
              <div className="flex gap-2">
                <Input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Ask about this deal..."
                  disabled={sendingChat}
                  data-testid="chat-input"
                  className="flex-1 bg-zinc-800 border-zinc-700 text-zinc-50"
                />
                <Button 
                  type="submit" 
                  disabled={sendingChat || !chatInput.trim()}
                  data-testid="send-chat-btn"
                  className="bg-white text-zinc-900 hover:bg-zinc-200"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default DealRoomDetail;
